import { NavLink, Outlet, useLocation } from 'react-router'
import { useAuth } from '@/features/auth/useAuth'
import { useUnreadTotal } from '@/features/chat/useUnreadTotal'
import { Avatar } from '@/components/ui'
import { cx } from '@/lib/cx'
import { useT } from '@/i18n/useT'
import { InstallPrompt } from '@/components/layout/InstallPrompt'
import { useFriendRequests } from '@/features/people/useFriends'

const TABS = [
  { to: '/', labelKey: 'nav.home' as const, icon: 'M3 10.5 12 3l9 7.5V21H3z' },
  { to: '/chats', labelKey: 'nav.chats' as const, icon: 'M4 5h16v11H8l-4 4z' },
  {
    to: '/people',
    labelKey: 'nav.people' as const,
    icon: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8M4 21a8 8 0 0 1 16 0',
  },
] as const

export function AppShell() {
  const { profile, user } = useAuth()
  const t = useT()
  const unread = useUnreadTotal()
  const { requests } = useFriendRequests()
  const { pathname } = useLocation()

  // A single conversation owns the whole screen on mobile; the tab bar would
  // just eat space above the composer.
  const hideTabs = /^\/chats\/[^/]+$/.test(pathname)

  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col">
      <main
        className={cx(
          'min-h-0 flex-1 overflow-y-auto',
          // Only when this main is itself the scrolling region a page relies
          // on (no tab bar below it to hand the inset to instead) -- a hidden-
          // tab-bar page like Conversation fills main with its own h-full
          // layout and carries the inset on its own scroller, so applying it
          // here too would just shrink that page for nothing.
          !hideTabs && 'safe-bottom-scroll',
        )}
      >
        <Outlet />
      </main>

      {!hideTabs && <InstallPrompt />}

      {!hideTabs && (
        // A fixed row height (h-12 on each link, not padding) rather than one
        // sized by its content: padding alone let the bar's height drift with
        // the label's line height, which differs slightly by browser and by
        // language. The home-indicator inset is not applied here at all --
        // see .safe-bottom-scroll above -- so this bar's own height can never
        // depend on a safe-area value that iOS might momentarily get wrong.
        <nav className="surface border-hair border-t pt-[5px]">
          <ul className="flex items-stretch">
            {TABS.map((tab) => (
              <li key={tab.to} className="flex-1">
                <NavLink to={tab.to} end={tab.to === '/'} className="block">
                  {({ isActive }) => (
                    <span
                      className={cx(
                        'flex h-12 flex-col items-center justify-center gap-0.5 text-[10px] font-semibold transition-colors',
                        isActive ? 'text-brand-500' : 'text-muted',
                      )}
                    >
                      <span
                        className={cx(
                          'relative flex items-center justify-center rounded-full px-2.5 py-0.5 transition-colors',
                          isActive && 'bg-brand-500/15',
                        )}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          className="size-6"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinejoin="round"
                        >
                          <path d={tab.icon} />
                        </svg>
                        {tab.to === '/chats' && unread > 0 && <Badge n={unread} />}
                        {tab.to === '/people' && requests.length > 0 && <Badge n={requests.length} />}
                      </span>
                      {t(tab.labelKey)}
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
            <li className="flex-1">
              <NavLink to="/settings" className="block">
                {({ isActive }) => (
                  <span
                    className={cx(
                      'flex h-12 flex-col items-center justify-center gap-0.5 text-[10px] transition-colors',
                      isActive ? 'text-brand-500' : 'text-muted',
                    )}
                  >
                    <span
                      className={cx(
                        'flex items-center justify-center rounded-full px-2.5 py-0.5 transition-colors',
                        isActive && 'bg-brand-500/15',
                      )}
                    >
                      <Avatar
                        id={user?.id ?? 'me'}
                        name={profile?.display_name ?? 'You'}
                        src={profile?.avatar_url}
                        size={24}
                      />
                    </span>
                    {t('nav.you')}
                  </span>
                )}
              </NavLink>
            </li>
          </ul>
        </nav>
      )}
    </div>
  )
}

function Badge({ n }: { n: number }) {
  return (
    <span className="bg-brand-500 absolute -top-1 -right-2 min-w-4 rounded-full px-1 text-[10px] leading-4 font-semibold text-white">
      {n > 99 ? '99+' : n}
    </span>
  )
}
